import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, PowerOff, Power, Search } from "lucide-react";
import { inr } from "@/utils";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePagination } from "@/components/Pagination";

const CATEGORIES = ["Supplier", "Contractor", "Consultant", "Service Provider", "Labour Contractor", "Sub Contractor"];
const EMPTY = {
  name: "", category: "Supplier", gst: "", pan: "", phone: "", email: "",
  contact_person: "", address: "",
  bank_name: "", bank_account_no: "", bank_ifsc: "",
  payment_terms: "Net 30", opening_balance: 0, notes: "", active: true,
};

export default function Vendors() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = vendors.filter((v) => {
    if (!showInactive && v.active === false) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (v.name || "").toLowerCase().includes(q) ||
      (v.category || "").toLowerCase().includes(q) ||
      (v.gst || "").toLowerCase().includes(q) ||
      (v.pan || "").toLowerCase().includes(q) ||
      (v.contact_person || "").toLowerCase().includes(q) ||
      (v.phone || "").toLowerCase().includes(q) ||
      (v.email || "").toLowerCase().includes(q)
    );
  });

  const { pageRows, controls } = usePagination(filtered);

  function openNew() { setForm(EMPTY); setEditId(null); setOpen(true); }
  function openEdit(v) { setForm({ ...EMPTY, ...v }); setEditId(v.id); setOpen(true); }

  function invalidate() {
    ["vendors", "vendor-ledger", "purchases"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  }

  async function save() {
    if (!form.name) return toast.error("Vendor name required");
    try {
      const payload = { ...form, opening_balance: +form.opening_balance || 0 };
      if (editId) {
        payload._reason = window.prompt("Reason for edit (audit)?", "") || "Edited";
        await api.patch(`/vendors/${editId}`, payload);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", payload);
        toast.success("Vendor added");
      }
      invalidate();
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function toggleActive(v) {
    try {
      await api.patch(`/vendors/${v.id}`, { active: !(v.active !== false), _reason: v.active === false ? "Reactivated" : "Marked inactive" });
      toast.success(v.active === false ? "Vendor reactivated" : "Vendor marked inactive");
      invalidate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function del(v) {
    if (!window.confirm(`Permanently delete vendor "${v.name}"?\n\nNote: If they have purchases/payments, delete will be blocked. Use "Mark Inactive" instead.`)) return;
    try {
      await api.delete(`/vendors/${v.id}`);
      toast.success("Vendor deleted");
      invalidate();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed";
      if (user?.role === "Admin" && detail.includes("purchases")) {
        if (window.confirm(`${detail}\n\nAs Admin, do you want to FORCE DELETE anyway?`)) {
          try { await api.delete(`/vendors/${v.id}`, { params: { force: true } }); toast.success("Force-deleted"); invalidate(); }
          catch (er) { toast.error(er?.response?.data?.detail || "Failed"); }
        }
      } else { toast.error(detail); }
    }
  }

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Procurement</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Vendors</h1>
          <p className="text-sm text-zinc-500 mt-1">Suppliers, contractors and consultants — linked to every purchase and expense.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              placeholder="Search name, GST, contact, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-64 pl-8"
              data-testid="vendor-search"
            />
          </div>
          <label className="flex items-center gap-1 text-xs text-zinc-500 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="show-inactive-vendors" /> Show inactive
          </label>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-vendor-btn"><Plus size={14} className="mr-1" /> Add Vendor</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Vendor" : "New Vendor"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name" /></div>
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="vendor-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
                <div><Label>Phone / Mobile</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>GSTIN</Label><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
                <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} /></div>
                <div><Label>Payment Terms</Label><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></div>
                <div><Label>Opening Balance (₹)</Label><Input type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} /></div>
                <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>

                <div className="col-span-2 mt-2"><div className="text-[10px] uppercase tracking-widest text-zinc-500 border-b pb-1">Bank Details</div></div>
                <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></div>
                <div><Label>Account Number</Label><Input value={form.bank_account_no} onChange={(e) => setForm({ ...form, bank_account_no: e.target.value })} /></div>
                <div><Label>IFSC</Label><Input value={form.bank_ifsc} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value })} /></div>
                <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-vendor">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-white border-zinc-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>GSTIN / PAN</TableHead>
              <TableHead>Contact</TableHead><TableHead>Terms</TableHead><TableHead className="text-right">Opening</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pageRows.map((v) => {
                const inactive = v.active === false;
                return (
                  <TableRow key={v.id} className={`data-row ${inactive ? "opacity-50" : ""}`} data-testid={`vendor-row-${v.id}`}>
                    <TableCell className="font-medium">{v.name}{inactive && <span className="ml-2 text-[10px] bg-zinc-200 text-zinc-700 px-1.5 rounded">INACTIVE</span>}</TableCell>
                    <TableCell className="text-xs uppercase tracking-widest">{v.category}</TableCell>
                    <TableCell className="text-xs font-mono">{v.gst || v.pan || "—"}</TableCell>
                    <TableCell className="text-xs">{v.phone}<br /><span className="text-zinc-500">{v.email}</span></TableCell>
                    <TableCell className="text-xs">{v.payment_terms}</TableCell>
                    <TableCell className="text-right font-mono">{inr(v.opening_balance)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="outline" size="sm" className="mr-1" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.id}`}><Pencil size={12} /></Button>
                      <Button variant="outline" size="sm" className="mr-1" onClick={() => toggleActive(v)} data-testid={`toggle-active-vendor-${v.id}`}>
                        {inactive ? <Power size={12} /> : <PowerOff size={12} />}
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => del(v)} data-testid={`delete-vendor-${v.id}`}><Trash2 size={12} /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!filtered.length && <TableRow><TableCell colSpan={7} className="text-center text-sm text-zinc-500 py-16">No vendors yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
          {controls}
        </CardContent>
      </Card>
    </div>
  );
}
